"use client"

import { useState, useCallback, useEffect, useMemo, useRef } from "react"
import { useSession } from "next-auth/react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { formatValue, isDualMeasurementUnit, shuffleArray } from "@/lib/utils"
import { getSafeGameItemDescription } from "@/lib/game-item-description"
import { useCountAnimation } from "@/lib/use-count-animation"
import {
  ChevronUp,
  ChevronDown,
  Home,
  RotateCcw,
  Trophy,
  X,
  Check,
  Zap,
  Timer,
  Loader2,
  Volume2,
  VolumeX
} from "lucide-react"
import { ShareButton } from "@/components/share/share-button"
import { AchievementToast } from "@/components/ui/achievement-toast"
import { GameItemImage } from "@/components/game/game-item-image"
import { GameItemPrompt } from "@/components/game/game-item-prompt"
import { HousingFacts } from "@/components/game/housing-facts"
import { StadiumFacts } from "@/components/game/stadium-facts"
import {
  preloadImage,
  preloadImages,
  waitForImageReady,
} from "@/lib/preload-image"
import { playUrgentTickSound, playSelectSound, playCorrectSound, playWrongSound, playGameOverSound, playVictorySound, isSoundMuted, setSoundMuted } from "@/lib/sounds"
import { trackProductEvent } from "@/lib/analytics/client"

interface GameItem {
  id: string
  name: string
  image: string | null
  images?: string[]
  value: number
  description: string | null
  floorAreaM2?: number
  houseType?: string
  housingLocation?: string
  stadiumSports?: string[]
  stadiumTeams?: string[]
}

interface GameType {
  id: string
  name: string
  slug?: string
  valueLabel: string
  valueUnit: string | null
  valuePrefix: boolean
  category: {
    name: string
    slug: string
  }
}

const categoryGradients: Record<string, { from: string; to: string }> = {
  technology: { from: "from-emerald-900/80", to: "to-emerald-950/90" },
  entertainment: { from: "from-rose-900/80", to: "to-rose-950/90" },
  gaming: { from: "from-orange-900/80", to: "to-rose-950/90" },
  "real-estate": { from: "from-amber-900/80", to: "to-amber-950/90" },
  sports: { from: "from-sky-900/80", to: "to-sky-950/90" },
  music: { from: "from-rose-900/80", to: "to-red-950/90" },
  cars: { from: "from-orange-900/80", to: "to-orange-950/90" },
  geography: { from: "from-teal-900/80", to: "to-teal-950/90" },
  movies: { from: "from-sky-900/80", to: "to-slate-950/90" },
  "social-media": { from: "from-pink-900/80", to: "to-pink-950/90" },
  "dutch-houses": { from: "from-amber-900/80", to: "to-amber-950/90" },
}

const defaultGradient = { from: "from-zinc-800/80", to: "to-zinc-900/90" }
const ROUND_REVEAL_DURATION_MS = 2300
const FAIL_REVEAL_DURATION_MS = 3500

interface Subtype {
  id: string
  name: string
}

interface GameContainerProps {
  gameType: GameType
  subtype?: Subtype
  initialItems: GameItem[]
  timerConfig?: { seconds: number }
}

type GameState = "playing" | "correct" | "wrong" | "gameover"

export function GameContainer({ gameType, subtype, initialItems, timerConfig }: GameContainerProps) {
  const { data: session } = useSession()
  // The server supplies a shuffled deck. Rendering its first pair immediately
  // puts the image preload/srcset into the initial HTML instead of waiting for
  // hydration and an effect before the browser can start downloading.
  const [items, setItems] = useState<GameItem[]>(() => [...initialItems])
  const [usedIds, setUsedIds] = useState<string[]>(() =>
    initialItems.slice(0, 2).map((item) => item.id)
  )
  const [currentItem, setCurrentItem] = useState<GameItem | null>(
    initialItems[0] ?? null
  )
  const [nextItem, setNextItem] = useState<GameItem | null>(
    initialItems[1] ?? null
  )
  const [score, setScore] = useState(0)
  const [highScore, setHighScore] = useState(0)
  const [gameState, setGameState] = useState<GameState>("playing")
  const [revealedValue, setRevealedValue] = useState<number | null>(null)
  const [unlockedAchievements, setUnlockedAchievements] = useState<{ id: string; code: string; name: string; description: string; icon: string; rarity: string; points: number }[]>([])
  const [nextPanelOpacity, setNextPanelOpacity] = useState(1)
  const [deckCleared, setDeckCleared] = useState(false)
  const [muted, setMutedState] = useState(false)

  // Timer state (deadline-based so it can't drift or pause in background tabs)
  const [timeLeft, setTimeLeft] = useState(timerConfig?.seconds ?? 0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const deadlineRef = useRef(0)
  const lastTickRef = useRef(-1)
  const completionTrackedRef = useRef(false)
  const gameStartedAtRef = useRef(Date.now())
  const scoreRunIdRef = useRef<string | null>(null)
  const totalTime = timerConfig?.seconds ?? 0
  const isHousingGame =
    gameType.category.slug === "real-estate" ||
    gameType.category.slug === "dutch-houses"
  const isStadiumGame =
    gameType.category.slug === "sports" &&
    gameType.slug === "stadium-capacity"

  useEffect(() => {
    preloadImages([
      ...(currentItem?.images?.slice(1) ?? []),
      ...(nextItem?.images?.slice(1) ?? []),
    ])
  }, [currentItem, nextItem])

  // Persist the best score per game/subtype/timer variant
  const bestKey = `hl-best:${gameType.id}:${subtype?.id ?? "all"}:${timerConfig?.seconds ?? "classic"}`

  useEffect(() => {
    setMutedState(isSoundMuted())
    try {
      const stored = Number(localStorage.getItem(bestKey))
      if (Number.isFinite(stored) && stored > 0) setHighScore(stored)
    } catch {
      // Start from 0 when storage is corrupt or unavailable
    }
  }, [bestKey])

  const toggleMute = () => {
    const next = !muted
    setMutedState(next)
    setSoundMuted(next)
  }

  const recordBest = useCallback((finalScore: number) => {
    setHighScore((prev) => Math.max(prev, finalScore))
    try {
      const stored = Number(localStorage.getItem(bestKey)) || 0
      if (finalScore > stored) localStorage.setItem(bestKey, String(finalScore))
    } catch {
      // Best score just won't persist
    }
  }, [bestKey])

  const getScoreRunId = useCallback(() => {
    if (!scoreRunIdRef.current) {
      scoreRunIdRef.current = crypto.randomUUID()
    }
    return scoreRunIdRef.current
  }, [])

  const animatedValue = useCountAnimation(revealedValue, 800)

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const startTimer = useCallback(() => {
    if (!timerConfig) return
    stopTimer()
    setTimeLeft(timerConfig.seconds)
    deadlineRef.current = Date.now() + timerConfig.seconds * 1000
    lastTickRef.current = timerConfig.seconds
    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000))
      if (remaining !== lastTickRef.current) {
        lastTickRef.current = remaining
        if (remaining <= 3 && remaining > 0) playUrgentTickSound()
        setTimeLeft(remaining)
      }
    }, 250)
  }, [timerConfig, stopTimer])

  const initializeGame = useCallback(() => {
    stopTimer()
    const shuffled = shuffleArray(initialItems)
    setItems(shuffled)
    setCurrentItem(shuffled[0])
    setNextItem(shuffled[1])
    setUsedIds([shuffled[0].id, shuffled[1].id])
    void preloadImage(shuffled[2]?.image, "low")
    setScore(0)
    setGameState("playing")
    setRevealedValue(null)
    setNextPanelOpacity(1)
    setDeckCleared(false)
    completionTrackedRef.current = false
    gameStartedAtRef.current = Date.now()
    scoreRunIdRef.current = null
    if (timerConfig) setTimeLeft(timerConfig.seconds)
    trackProductEvent("game_started", {
      gameTypeId: gameType.id,
      properties: {
        mode: timerConfig ? "timed" : "standard",
        subtypeId: subtype?.id || null,
        category_slug: gameType.category.slug,
        game_slug: gameType.slug || null,
        item_count: initialItems.length,
      },
    })
  }, [gameType.category.slug, gameType.id, gameType.slug, initialItems, subtype?.id, timerConfig, stopTimer])

  useEffect(() => {
    // Leave bandwidth for the visible pair. Later rounds preload on a guess.
    void preloadImage(initialItems[2]?.image, "low")
    trackProductEvent("game_started", {
      gameTypeId: gameType.id,
      properties: {
        mode: timerConfig ? "timed" : "standard",
        subtypeId: subtype?.id || null,
        category_slug: gameType.category.slug,
        game_slug: gameType.slug || null,
        item_count: initialItems.length,
      },
    })
  }, [
    gameType.category.slug,
    gameType.id,
    gameType.slug,
    initialItems,
    subtype?.id,
    timerConfig,
  ])

  useEffect(() => {
    if (gameState !== "gameover" || completionTrackedRef.current) return
    completionTrackedRef.current = true
    trackProductEvent("game_completed", {
      gameTypeId: gameType.id,
      properties: {
        mode: timerConfig ? "timed" : "standard",
        subtypeId: subtype?.id || null,
        score,
        deckCleared,
        rounds_answered: score + (deckCleared ? 0 : 1),
        duration_seconds: Math.round(
          (Date.now() - gameStartedAtRef.current) / 100,
        ) / 10,
        category_slug: gameType.category.slug,
        game_slug: gameType.slug || null,
      },
    })
  }, [deckCleared, gameState, gameType.category.slug, gameType.id, gameType.slug, score, subtype?.id, timerConfig])

  // Timer management
  useEffect(() => {
    if (!timerConfig) return
    if (gameState === "playing") {
      startTimer()
    } else {
      stopTimer()
    }
    return stopTimer
  }, [gameState, timerConfig, startTimer, stopTimer])

  // Handle time running out
  useEffect(() => {
    if (!timerConfig || timeLeft !== 0 || gameState !== "playing") return
    stopTimer()
    setRevealedValue(nextItem?.value ?? null)
    setGameState("wrong")
    trackProductEvent("game_answered", {
      gameTypeId: gameType.id,
      properties: {
        mode: "timed",
        subtypeId: subtype?.id || null,
        guess: "timeout",
        correct: false,
        round: score + 1,
        score,
      },
    })
    playWrongSound()
    setTimeout(() => {
      setGameState("gameover")
      playGameOverSound()
      recordBest(score)
      if (session) {
        fetch("/api/scores", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            runId: getScoreRunId(),
            score,
            gameTypeId: gameType.id,
            subtypeId: subtype?.id,
            mode: "timed",
            timePerRound: timerConfig.seconds
          })
        })
          .then(res => res.json())
          .then(data => {
            if (data.newAchievements?.length) setUnlockedAchievements(data.newAchievements)
          })
          .catch(() => {})
      }
    }, FAIL_REVEAL_DURATION_MS)
  }, [timeLeft, gameState, timerConfig, score, recordBest, session, gameType.id, subtype?.id, nextItem, stopTimer, getScoreRunId])

  const getNextUnusedItem = useCallback((): GameItem | null => {
    const available = items.filter((item) => !usedIds.includes(item.id))
    if (available.length === 0) {
      const newItems = items.filter(
        (item) => item.id !== currentItem?.id && item.id !== nextItem?.id
      )
      if (newItems.length === 0) return null
      setUsedIds([currentItem?.id || "", nextItem?.id || ""])
      return newItems[Math.floor(Math.random() * newItems.length)]
    }
    // The deck is already shuffled. Taking the first unused item makes the
    // following panel predictable enough to preload one round ahead.
    return available[0]
  }, [items, usedIds, currentItem, nextItem])

  const handleGuess = async (guess: "higher" | "lower") => {
    if (!currentItem || !nextItem || gameState !== "playing") return

    playSelectSound()
    if (timerConfig) stopTimer()

    const isCorrect =
      guess === "higher"
        ? nextItem.value >= currentItem.value
        : nextItem.value <= currentItem.value

    trackProductEvent("game_answered", {
      gameTypeId: gameType.id,
      properties: {
        mode: timerConfig ? "timed" : "standard",
        subtypeId: subtype?.id || null,
        guess,
        correct: isCorrect,
        round: score + 1,
        score_after: isCorrect ? score + 1 : score,
      },
    })

    setRevealedValue(nextItem.value)

    if (isCorrect) {
      setGameState("correct")
      setScore((prev) => prev + 1)
      playCorrectSound()

      const newNextItem = getNextUnusedItem()
      const lookaheadItem = items.find(
        (item) =>
          !usedIds.includes(item.id) &&
          item.id !== newNextItem?.id
      )
      void preloadImage(newNextItem?.image, "high")
      void preloadImages(newNextItem?.images?.slice(1) ?? [])
      void preloadImage(lookaheadItem?.image, "high")
      void preloadImages(lookaheadItem?.images?.slice(1) ?? [])

      if (!newNextItem) {
        // End the run as a win when the player gets through every item
        const finalScore = score + 1
        setTimeout(() => {
          setDeckCleared(true)
          setGameState("gameover")
          playVictorySound()
          recordBest(finalScore)
          if (session) {
            fetch("/api/scores", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                runId: getScoreRunId(),
                score: finalScore,
                gameTypeId: gameType.id,
                subtypeId: subtype?.id,
                ...(timerConfig ? { mode: "timed" } : {})
              })
            })
              .then(res => res.json())
              .then(data => {
                if (data.newAchievements?.length) setUnlockedAchievements(data.newAchievements)
              })
              .catch(() => {})
          }
        }, ROUND_REVEAL_DURATION_MS)
        return
      }

      setTimeout(async () => {
        // The normal reveal interval absorbs almost every request. On a slow
        // connection, allow a short final decode window before swapping.
        await waitForImageReady(newNextItem.image)
        // Brief crossfade
        setNextPanelOpacity(0)
        setTimeout(() => {
          setUsedIds((prev) => [...prev, newNextItem.id])
          setCurrentItem(nextItem)
          setNextItem(newNextItem)
          setRevealedValue(null)
          if (timerConfig) setTimeLeft(timerConfig.seconds)
          setGameState("playing")
          // Fade back in
          requestAnimationFrame(() => setNextPanelOpacity(1))
        }, 200)
      }, ROUND_REVEAL_DURATION_MS)
    } else {
      setGameState("wrong")
      playWrongSound()
      setTimeout(() => {
        setGameState("gameover")
        playGameOverSound()
        recordBest(score)
        if (session) {
          const body: Record<string, unknown> = {
            runId: getScoreRunId(),
            score,
            gameTypeId: gameType.id,
            subtypeId: subtype?.id
          }
          if (timerConfig) {
            body.mode = "timed"
            body.timePerRound = timerConfig.seconds
          }
          fetch("/api/scores", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
          })
            .then(res => res.json())
            .then(data => {
              if (data.newAchievements?.length) {
                setUnlockedAchievements(data.newAchievements)
              }
            })
            .catch(() => {})
        }
      }, FAIL_REVEAL_DURATION_MS)
    }
  }

  // Keyboard controls: arrows or H/L to guess, Enter/Space to restart
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return

      if (gameState === "playing") {
        const key = e.key.toLowerCase()
        if (e.key === "ArrowUp" || key === "h") {
          e.preventDefault()
          handleGuess("higher")
        } else if (e.key === "ArrowDown" || key === "l") {
          e.preventDefault()
          handleGuess("lower")
        }
      } else if (gameState === "gameover" && e.key === "Enter") {
        e.preventDefault()
        initializeGame()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  })

  const displayValue = (value: number) => {
    return formatValue(value, gameType.valueUnit || undefined, gameType.valuePrefix)
  }
  const valueTextSize = isDualMeasurementUnit(gameType.valueUnit)
    ? "text-2xl sm:text-3xl md:text-4xl"
    : "text-4xl md:text-5xl"

  // Memoized: these run inside the 60fps count-up reveal re-renders
  const currentDescription = useMemo(
    () =>
      currentItem
        ? getSafeGameItemDescription({
            description: currentItem.description,
            value: currentItem.value,
            valueLabel: gameType.valueLabel,
            valueUnit: gameType.valueUnit,
            valuePrefix: gameType.valuePrefix,
          })
        : null,
    [currentItem, gameType.valueLabel, gameType.valueUnit, gameType.valuePrefix]
  )
  const nextDescription = useMemo(
    () =>
      nextItem
        ? getSafeGameItemDescription({
            description: nextItem.description,
            value: nextItem.value,
            valueLabel: gameType.valueLabel,
            valueUnit: gameType.valueUnit,
            valuePrefix: gameType.valuePrefix,
          })
        : null,
    [nextItem, gameType.valueLabel, gameType.valueUnit, gameType.valuePrefix]
  )

  // Timer visuals
  const timerPercent = totalTime > 0 ? timeLeft / totalTime : 0
  const timerColor =
    timerPercent > 0.5 ? "text-emerald-400" : timerPercent > 0.25 ? "text-amber-400" : "text-rose-400"
  const timerBgColor =
    timerPercent > 0.5 ? "bg-emerald-500" : timerPercent > 0.25 ? "bg-amber-500" : "bg-rose-500"

  if (!currentItem || !nextItem) {
    return (
      <div className="studio-game-stage min-h-dvh flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 text-[#b83f34] animate-spin" />
      </div>
    )
  }

  const valuesAreEqual = nextItem.value === currentItem.value
  const relativeGap = currentItem.value !== 0
    ? Math.abs(nextItem.value - currentItem.value) / Math.abs(currentItem.value)
    : 1

  return (
    <div role="main" className="studio-game-stage flex h-dvh flex-col overflow-hidden bg-zinc-950">
      <h1 className="sr-only">
        {gameType.name}{subtype ? `: ${subtype.name}` : ""} Higher or Lower Game
      </h1>
      {/* Header */}
      <header className="studio-game-hud fixed left-0 right-0 top-0 z-50 px-3 py-3 md:px-5 md:py-4">
        <div className="studio-game-hud-shell mx-auto grid max-w-6xl grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-2 rounded-2xl border border-white/70 bg-[#fbf7ef]/95 p-2 shadow-[0_16px_45px_rgba(38,28,18,0.16)] backdrop-blur-xl sm:grid-cols-[minmax(52px,1fr)_minmax(0,20rem)_minmax(52px,1fr)] md:gap-4 md:px-3">
          <Button asChild variant="ghost" size="sm" className="studio-game-hud-button h-10 w-10 gap-2 rounded-xl border border-[#ddd2c5] bg-white/70 px-0 text-[#25211d] shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-white sm:h-11 sm:w-auto sm:px-3 md:px-4 justify-self-start">
            <Link href="/play" aria-label="Exit game">
              <Home className="size-4" aria-hidden="true" />
              <span className="hidden sm:inline">Exit game</span>
            </Link>
          </Button>

          <div
            className="flex w-full min-w-0 items-center justify-self-center overflow-hidden rounded-xl border border-[#e5d9ca] bg-[#f0e8dc] px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] sm:py-2 md:w-72 md:px-4"
            aria-label={
              timerConfig
                ? `${timerConfig.seconds} second timed game`
                : `${gameType.category.name}: ${gameType.name}${
                    subtype ? `, ${subtype.name}` : ""
                  }`
            }
          >
            {timerConfig ? (
              <>
                <span className="mr-2 grid size-7 shrink-0 place-items-center rounded-lg bg-[#ff6f61] text-white shadow-sm">
                  <Timer className="size-3.5" aria-hidden="true" />
                </span>
                <span className="min-w-0 leading-tight">
                  <span className="hidden text-[10px] font-bold uppercase tracking-[0.13em] text-[#625a52] md:block">
                    Timed game
                  </span>
                  <strong className="block truncate text-sm text-[#25211d] md:text-[15px]">
                    {timerConfig.seconds}s timer
                  </strong>
                </span>
              </>
            ) : (
              <>
                <span className="mr-2 grid size-7 shrink-0 place-items-center rounded-lg bg-[#ff6f61] text-white shadow-sm">
                  <Zap className="size-3.5" aria-hidden="true" />
                </span>
                <span className="min-w-0 leading-tight">
                  <span className="hidden text-[10px] font-bold uppercase tracking-[0.13em] text-[#625a52] sm:block">
                    {gameType.category.name}
                  </span>
                  <strong className="block truncate text-sm text-[#25211d] md:text-[15px]">
                    <span className="hidden">{gameType.category.name}</span>
                    <span className="block">
                      {gameType.name}
                      {subtype && ` / ${subtype.name}`}
                    </span>
                  </strong>
                </span>
              </>
            )}
          </div>

          <div className="flex min-w-0 items-center justify-self-end gap-1 md:gap-2">
            <button
              onClick={toggleMute}
              aria-label={muted ? "Unmute sounds" : "Mute sounds"}
              className="studio-game-hud-button grid size-9 shrink-0 place-items-center rounded-xl border border-[#ddd2c5] bg-white/70 text-[#625a52] shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-white sm:size-10"
            >
              {muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
            </button>

            {/* Timer Display */}
            {timerConfig && gameState === "playing" && (
              <div className="hidden items-center md:flex">
                <div className="relative size-10">
                  <svg className="size-10 -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="2" className="text-[#ded4c8]" />
                    <circle
                      cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="2.5"
                      className={timerColor}
                      strokeDasharray={`${timerPercent * 97.4} 97.4`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className={`absolute inset-0 flex items-center justify-center text-sm font-bold ${timerColor}`}>
                    {timeLeft}
                  </span>
                </div>
              </div>
            )}

            <div className="studio-game-hud-score min-w-[3.25rem] rounded-xl bg-[#ff6f61] px-2 py-1.5 text-center text-white shadow-[0_6px_16px_rgba(184,63,52,0.24)] sm:min-w-14 sm:px-3 md:min-w-16">
              <div className="text-xl font-black leading-none md:text-2xl">{score}</div>
              <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white/75">Score</div>
            </div>
            {highScore > 0 && (
              <div className="hidden min-w-14 rounded-xl border border-[#ddd2c5] bg-white/70 px-3 py-1.5 text-center sm:block">
                <div className="text-lg font-black leading-none text-[#25211d]">{highScore}</div>
                <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-[#756b61]">Best</div>
              </div>
            )}
          </div>
        </div>

        {/* Timer Bar */}
        {timerConfig && gameState === "playing" && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800">
            <div
              className={`h-full transition-all duration-1000 linear ${timerBgColor}`}
              style={{ width: `${timerPercent * 100}%` }}
            />
          </div>
        )}
      </header>

      {/* Game Area */}
      <div className="flex min-h-0 flex-1 flex-col pt-[80px] sm:pt-[84px] md:flex-row md:pt-24">
        {/* Current Item */}
        <div
          className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-3 py-2 sm:p-6 md:p-12"
        >
          <GameItemImage
            src={currentItem.image}
            sources={currentItem.images}
            itemName={currentItem.name}
            gameTypeName={gameType.name}
            gameTypeSlug={gameType.slug}
            categorySlug={gameType.category.slug}
            fallbackClassName={`bg-gradient-to-br ${(categoryGradients[gameType.category.slug] || defaultGradient).from} ${(categoryGradients[gameType.category.slug] || defaultGradient).to}`}
            priority
          />
          <div className="relative z-10 w-full max-w-xl text-center text-white drop-shadow-[0_3px_12px_rgba(0,0,0,0.85)]">
            {!(currentItem.floorAreaM2 && currentItem.houseType) && (
              <GameItemPrompt
                label="Current"
                name={currentItem.name}
                description={currentDescription}
              />
            )}
            <HousingFacts
              floorAreaM2={currentItem.floorAreaM2}
              houseType={currentItem.houseType}
              location={currentItem.housingLocation}
              statusLabel="Current property"
            />
            <StadiumFacts
              sports={currentItem.stadiumSports}
              teams={currentItem.stadiumTeams}
            />
            <div className={`inline-block rounded-2xl border px-8 py-4 backdrop-blur-xl ${
              isHousingGame || isStadiumGame
                ? "border-white/30 bg-black/60 shadow-[0_14px_40px_rgba(0,0,0,0.28)]"
                : "border-white/20 bg-white/10"
            }`}>
              <div className="text-sm text-white/60 mb-1">{gameType.valueLabel}</div>
              <div className={`${valueTextSize} font-black`}>{displayValue(currentItem.value)}</div>
            </div>
          </div>
        </div>

        {/* VS Divider */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 hidden md:flex">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 ${
            gameState === "correct"
              ? "bg-emerald-500 scale-110"
              : gameState === "wrong"
                ? "bg-rose-500 scale-110"
                : "bg-white"
          }`}>
            {gameState === "correct" ? (
              <Check className="h-10 w-10 text-white" />
            ) : gameState === "wrong" ? (
              <X className="h-10 w-10 text-white" />
            ) : (
              <span className="text-2xl font-black text-zinc-900">VS</span>
            )}
          </div>
        </div>

        {/* Mobile VS */}
        <div className="z-20 -my-5 flex shrink-0 justify-center md:hidden">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 ${
            gameState === "correct"
              ? "bg-emerald-500"
              : gameState === "wrong"
                ? "bg-rose-500"
                : "bg-white"
          }`}>
            {gameState === "correct" ? (
              <Check className="h-7 w-7 text-white" />
            ) : gameState === "wrong" ? (
              <X className="h-7 w-7 text-white" />
            ) : (
              <span className="text-lg font-black text-zinc-900">VS</span>
            )}
          </div>
        </div>

        {/* Next Item */}
        <div
          className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-3 py-2 transition-opacity duration-200 sm:p-6 md:p-12"
          style={{
            opacity: nextPanelOpacity,
          }}
        >
          <GameItemImage
            src={nextItem.image}
            sources={nextItem.images}
            itemName={nextItem.name}
            gameTypeName={gameType.name}
            gameTypeSlug={gameType.slug}
            categorySlug={gameType.category.slug}
            fallbackClassName={`bg-gradient-to-tl ${(categoryGradients[gameType.category.slug] || defaultGradient).from} ${(categoryGradients[gameType.category.slug] || defaultGradient).to}`}
            priority
          />
          <div className="relative z-10 w-full max-w-xl text-center text-white drop-shadow-[0_3px_12px_rgba(0,0,0,0.85)]">
            {!(nextItem.floorAreaM2 && nextItem.houseType) && (
              <GameItemPrompt
                label="Guess"
                name={nextItem.name}
                description={nextDescription}
              />
            )}
            <HousingFacts
              floorAreaM2={nextItem.floorAreaM2}
              houseType={nextItem.houseType}
              location={nextItem.housingLocation}
              statusLabel={`Guess the ${gameType.valueLabel.toLowerCase()}`}
              hiddenValueUnit={revealedValue === null ? gameType.valueUnit : null}
            />
            <StadiumFacts
              sports={nextItem.stadiumSports}
              teams={nextItem.stadiumTeams}
            />

            {revealedValue !== null ? (
              <div className={`inline-block px-8 py-4 rounded-2xl backdrop-blur-xl border transition-all duration-500 animate-value-reveal ${
                gameState === "correct"
                  ? isHousingGame || isStadiumGame
                    ? "border-emerald-300/50 bg-emerald-950/70"
                    : "bg-emerald-500/20 border-emerald-500/50"
                  : gameState === "wrong"
                    ? isHousingGame || isStadiumGame
                      ? "border-rose-300/50 bg-rose-950/70"
                      : "bg-rose-500/20 border-rose-500/50"
                    : isHousingGame || isStadiumGame
                      ? "border-white/30 bg-black/60"
                      : "bg-white/10 border-white/20"
              }`}>
                <div className="text-sm text-white/60 mb-1">{gameType.valueLabel}</div>
                <div className={`${valueTextSize} font-black ${
                  gameState === "correct" ? "text-emerald-400" : gameState === "wrong" ? "text-rose-400" : "text-white"
                }`}>
                  {animatedValue !== null ? displayValue(animatedValue) : displayValue(revealedValue)}
                </div>
                {gameState === "correct" && valuesAreEqual && (
                  <div className="text-xs font-semibold text-emerald-300 mt-2">
                    Equal values. That counts as correct!
                  </div>
                )}
                {gameState === "wrong" && relativeGap > 0 && relativeGap < 0.1 && (
                  <div className="text-xs font-semibold text-rose-300 mt-2">
                    So close. You were off by just {Math.max(1, Math.round(relativeGap * 100))}%!
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="flex flex-col justify-center gap-1.5 sm:flex-row sm:gap-3">
                  <Button
                    onClick={() => handleGuess("higher")}
                    disabled={gameState !== "playing"}
                    className="h-11 gap-2 rounded-xl bg-emerald-700 px-8 text-base font-bold text-white shadow-lg shadow-emerald-700/20 hover:bg-emerald-800 sm:h-14 sm:text-lg"
                  >
                    <ChevronUp className="size-5 sm:size-6" />
                    Higher
                  </Button>
                  <Button
                    onClick={() => handleGuess("lower")}
                    disabled={gameState !== "playing"}
                    className="h-11 gap-2 rounded-xl bg-rose-700 px-8 text-base font-bold text-white shadow-lg shadow-rose-700/20 hover:bg-rose-800 sm:h-14 sm:text-lg"
                  >
                    <ChevronDown className="size-5 sm:size-6" />
                    Lower
                  </Button>
                </div>
                {timerConfig && gameState === "playing" && (
                  <div className="w-full max-w-xs">
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-1000 linear ${
                          timerPercent > 0.5 ? "bg-emerald-500" : timerPercent > 0.25 ? "bg-amber-500" : "bg-rose-500"
                        }`}
                        style={{ width: `${timerPercent * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Game Over Modal */}
      {gameState === "gameover" && (
        <div role="dialog" aria-modal="true" aria-label="Game over" className="fixed inset-0 bg-black/90 backdrop-blur-xl flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-3xl p-8 md:p-12 max-w-md w-full max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain text-center border border-zinc-800">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8 ${
              deckCleared ? "bg-emerald-500/20" : timerConfig ? "bg-cyan-500/20" : "bg-rose-500/20"
            }`}>
              {deckCleared ? (
                <Trophy className="h-10 w-10 text-emerald-500" />
              ) : timerConfig ? (
                <Timer className="h-10 w-10 text-cyan-500" />
              ) : (
                <X className="h-10 w-10 text-rose-500" />
              )}
            </div>

            <h2 className="text-3xl font-bold text-white mb-3">
              {deckCleared ? "Deck Cleared!" : timerConfig ? "Time\u2019s Up!" : "Game Over!"}
            </h2>
            <p className="text-zinc-400 mb-2">
              {deckCleared ? (
                <>You beat every {gameType.name} matchup. A perfect run!</>
              ) : timerConfig ? gameType.name : (
                <>
                  {nextItem.name} has a {gameType.valueLabel.toLowerCase()} of <span className="text-white font-semibold">{displayValue(nextItem.value)}</span>
                </>
              )}
            </p>
            {timerConfig && (
              <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide mb-6 bg-cyan-500/20 text-cyan-400">
                {timerConfig.seconds}s per round
              </div>
            )}

            <div className="flex items-center justify-center gap-8 mb-10">
              <div>
                <div className="text-5xl font-black text-white">{score}</div>
                <div className="text-sm text-zinc-500 uppercase tracking-wide">Final Score</div>
              </div>
              {highScore > 0 && (
                <div className="pl-8 border-l border-zinc-800">
                  <div className="text-3xl font-bold text-zinc-500 flex items-center gap-2">
                    <Trophy className="h-6 w-6 text-amber-500" />
                    {highScore}
                  </div>
                  <div className="text-sm text-zinc-500 uppercase tracking-wide">Best</div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <ShareButton
                type={timerConfig ? "timed" : "standard"}
                score={score}
                gameType={gameType.name}
                {...(timerConfig ? { extra: { difficulty: `${timerConfig.seconds}s` } } : {})}
              />
              <Button
                onClick={initializeGame}
                autoFocus
                className="h-14 text-lg rounded-xl gap-2 bg-white text-zinc-900 hover:bg-zinc-100"
              >
                <RotateCcw className="h-5 w-5" />
                Play Again
              </Button>
              <Link href="/play" className="w-full">
                <Button variant="outline" className="studio-game-primary-action h-14 w-full rounded-xl text-lg">
                  Choose Different Game
                </Button>
              </Link>
            </div>

            {!session && (
              <p className="text-sm text-zinc-500 mt-8">
                <Link href="/login" className="text-white hover:underline">
                  Sign in
                </Link>{" "}
                to save your scores to the leaderboard
              </p>
            )}
          </div>
        </div>
      )}

      {/* Achievement Toasts */}
      <AchievementToast achievements={unlockedAchievements} onDismiss={() => setUnlockedAchievements([])} />

      {/* Screen reader announcements */}
      <div aria-live="polite" className="sr-only">
        {gameState === "correct" && `Correct! ${nextItem.name}: ${displayValue(nextItem.value)}. Score ${score}.`}
        {gameState === "wrong" && `Wrong. ${nextItem.name}: ${displayValue(nextItem.value)}.`}
        {gameState === "gameover" && `Game over. Final score ${score}.`}
      </div>
    </div>
  )
}
